import { useEffect, useState, useRef, useMemo } from "react";
import { ToastContainer } from "react-toastify";
import moment from "moment";
import ReactToPrint from "react-to-print";
import { useDispatch, useSelector } from "react-redux";
import {
  ArchiveFill,
  ArrowBarLeft,
  ArrowBarRight,
  ArrowClockwise,
  FiletypeCsv,
  FilterCircle,
  PenFill,
  PlayBtn,
  PrinterFill,
  ThreeDots,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { CSVLink } from "react-csv";
import { Accordion, Badge, Card, Collapse } from "react-bootstrap";

// custom hooks import
import useISPowner from "../../hooks/useISPOwner";
import useAreaPackage from "../../hooks/useAreaPackage";

// internal import
import {
  deleteBillReport,
  // getAllBills,
  getAllManagerBills,
  getArea,
  getMonthlyBillCollectionReport,
} from "../../features/apiCalls";
import Footer from "../../components/admin/footer/Footer";
import "../Customer/customer.css";
import "./report.css";
import { FontColor, FourGround } from "../../assets/js/theme";
import Sidebar from "../../components/admin/sidebar/Sidebar";
import useDash from "../../assets/css/dash.module.css";
import PrintReport from "./ReportPDF";
import Table from "../../components/table/Table";
import Loader from "../../components/common/Loader";
import EditReport from "./modal/EditReport";
import ReportView from "./modal/ReportView";
import FormatNumber from "../../components/common/NumberFormat";
import { getSubAreasApi } from "../../features/actions/customerApiCall";
import { managerFetchSuccess } from "../../features/managerSlice";
import NetFeeBulletin from "../../components/bulletin/NetFeeBulletin";
import { getBulletinPermission } from "../../features/apiCallAdmin";
import { userStaffs } from "../../features/getIspOwnerUsersApi";
import PlayTutorial from "../tutorial/PlayTutorial";
import { badge } from "../../components/common/Utils";
import useDataState from "../../hooks/useDataState";
import DataFilter from "../common/DataFilter";
import PaginatedTable from "../../components/table/PaginatedTable";

const Report = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const componentRef = useRef();

  // get user & current user data form useISPOwner hooks
  const { role, ispOwnerId, userData, permissions } = useISPowner();

  // get all area package data from useAreaPackage hooks
  const { areas, subAreas } = useAreaPackage();

  // get customer bill collection data from redux store
  const allBills = useSelector((state) => state?.payment?.allBills);

  // get user staff data from redux store
  const staffs = useSelector((state) => state?.ownerUsers?.userStaff);

  // get bulletin permission
  const butPermission = useSelector(
    (state) => state.adminNetFeeSupport?.bulletinPermission,
  );

  // current date state
  var today = new Date();
  var firstDay =
    role === "ispOwner" || permissions?.dashboardCollectionData
      ? new Date(today.getFullYear(), today.getMonth(), 1)
      : new Date();

  firstDay.setHours(0, 0, 0, 0);
  today.setHours(23, 59, 59, 999);

  const filterInputPermission = {
    month: true,
    mikrotik: true,
    package: true,
    area: true,
    subArea: true,
    poleBox: true,
    collectedBy: true,
    billType: true,
    billingType: true,
    medium: true,
    startCreateDate: true,
    endCreateDate: true,
    userType: true,
  };

  //---> Get filtered dataset from useDataState hook
  const { filterOptions, setFilterOption } = useDataState();

  // loading state
  const [isLoading, setIsLoading] = useState(false);
  const [areaLoading, setAreaLoading] = useState(false);
  const [billReport, setBillReport] = useState({
    data: [],
    totalPages: 0,
  });

  const [singleArea, setArea] = useState({});
  const [subAreaIds, setSubArea] = useState([]);
  const [open, setOpen] = useState(false);

  // modal close handler
  const [modalStatus, setModalStatus] = useState("");
  const [show, setShow] = useState(false);

  // filter Accordion handle state
  const [activeKeys, setActiveKeys] = useState("");

  // api call
  useEffect(() => {
    // get user staffs api
    staffs.length === 0 && userStaffs(dispatch);

    // get area api
    areas.length === 0 && getArea(dispatch, ispOwnerId, setAreaLoading);

    // get sub area api
    subAreas.length === 0 && getSubAreasApi(dispatch, ispOwnerId);

    // get netfee bulletin permission api
    Object.keys(butPermission)?.length === 0 && getBulletinPermission(dispatch);
  }, []);

  useEffect(() => {
    if (filterOptions?.month !== undefined) {
      var createStart = new Date(
        filterOptions?.month?.getFullYear(),
        filterOptions?.month?.getMonth(),
        1,
      );
      var createEnd = new Date(
        filterOptions?.month?.getFullYear(),
        filterOptions?.month?.getMonth() + 1,
        0,
      );
      createEnd.setHours(23, 59, 59, 999);

      if (createEnd?.getMonth() + 1 === today.getMonth() + 1) {
        setFilterOption({
          ...filterOptions,
          startCreateDate: createStart,
          endCreateDate: today,
        });
      } else {
        setFilterOption({
          ...filterOptions,
          startCreateDate: createStart,
          endCreateDate: createEnd,
        });
      }

      role === "ispOwner" &&
        getMonthlyBillCollectionReport(
          dispatch,
          filterOptions?.month?.getFullYear(),
          filterOptions?.month?.getMonth() + 1,
          setIsLoading,
        );

      role === "manager" &&
        getAllManagerBills(
          dispatch,
          ispOwnerId,
          filterOptions?.month?.getFullYear(),
          filterOptions?.month?.getMonth() + 1,
          setIsLoading,
        );
    }
  }, [filterOptions?.month]);

  // set data in state
  useEffect(() => {
    if (allBills) {
      setBillReport((prev) => {
        return {
          data: allBills?.data || [],
          page: allBills?.page || 1,
          limit: allBills?.limit || 100,
          ...(allBills?.total
            ? { total: allBills?.total }
            : { total: prev?.total || 0 }),
          ...(allBills?.totalPages
            ? { totalPages: allBills?.totalPages }
            : { totalPages: prev?.totalPages || 0 }),
          ...(allBills?.aggregate
            ? { aggregate: allBills?.aggregate }
            : { aggregate: prev?.aggregate || {} }),
          nextCursor: allBills?.nextCursor || null,
          previousCursor: allBills?.previousCursor || null,
        };
      });
    }
  }, [allBills]);

  // report page reload handler
  const reloadHandler = () => {
    if (role === "manager") {
      getAllManagerBills(
        dispatch,
        ispOwnerId,
        filterOptions?.month?.getFullYear(),
        filterOptions?.month?.getMonth() + 1,
        setIsLoading,
      );
      dispatch(managerFetchSuccess(userData));
    }

    role === "ispOwner" &&
      getMonthlyBillCollectionReport(
        dispatch,
        filterOptions?.month?.getFullYear(),
        filterOptions?.month?.getMonth() + 1,
        setIsLoading,
      );
  };

  const handleFetchServerFiltered = (query) => {
    const { pageIndex, pageSize, search } = query;

    const filteredQuery = Object.fromEntries(
      Object.entries({ search }).filter(
        ([_, value]) => value !== undefined && value !== null && value !== "",
      ),
    );

    const searchParams = new URLSearchParams(filteredQuery).toString();

    getMonthlyBillCollectionReport(
      dispatch,
      filterOptions?.month?.getFullYear(),
      filterOptions?.month?.getMonth() + 1,
      setIsLoading,
      pageIndex,
      pageSize,
      searchParams,
    );
  };

  const fetchPaginatedData = (pageIndex, pageSize, searchParams) => {
    if (filterOptions?.isServerFiltering) {
      const { month, ...rest } = filterOptions;

      const filteredQuery = Object.fromEntries(
        Object.entries({ ...rest }).filter(
          ([_, value]) => value !== undefined && value !== null && value !== "",
        ),
      );

      // Convert to URLSearchParams
      const searchQueryParams = new URLSearchParams(filteredQuery).toString();

      searchParams += `&${searchQueryParams}`;
    }

    getMonthlyBillCollectionReport(
      dispatch,
      filterOptions?.month?.getFullYear(),
      filterOptions?.month?.getMonth() + 1,
      setIsLoading,
      pageIndex,
      pageSize,
      searchParams,
    );
  };

  const filterBillsReport = (query) => {
    const { month, ...rest } = query;

    // Remove undefined / null values to keep URL clean
    const filteredQuery = Object.fromEntries(
      Object.entries({ ...rest }).filter(
        ([_, value]) => value !== undefined && value !== null && value !== "",
      ),
    );

    // Convert to URLSearchParams
    const searchParams = new URLSearchParams(filteredQuery).toString();

    setFilterOption({
      ...filterOptions,
      isServerFiltering: true,
    });

    getMonthlyBillCollectionReport(
      dispatch,
      month?.getFullYear(),
      month?.getMonth() + 1,
      setIsLoading,
      1,
      billReport?.pageSize,
      searchParams,
    );
  };

  // set Report id
  const [reportId, setReportId] = useState();
  const [viewId, setViewId] = useState();

  // note state
  const [note, setNote] = useState();

  // set Report function
  const getReportId = (reportID) => {
    setReportId(reportID);
    setNote("");
  };

  // delete customer collection report delete handler
  const collectionReportDeleteHandler = (reportId) => {
    const con = window.confirm(t("deleteAlert"));
    if (con) {
      deleteBillReport(dispatch, reportId);
    }
  };

  const area = areas.find((item) => item.id === filterOptions?.area);
  const subArea = subAreas.find((item) => item.id === filterOptions?.subArea);
  const collector = staffs.find(
    (item) => item.id === filterOptions?.collectedBy,
  );

  const filterDataHeader = {
    area: area?.name ? area.name : t("all"),
    subArea: subArea ? subArea.name : t("all"),
    collector: collector?.name ? collector.name : t("all"),
    startDate: filterOptions?.startCreateDate,
    endDate: filterOptions?.endCreateDate,
    totalBill: billReport?.data?.reduce(
      (prev, current) => prev + current.amount,
      0,
    ),
  };

  // csv start
  // get data from main data for csv
  let reportForCsVTableInfo = billReport?.data.map((data) => {
    const note = data?.note ? data?.note : "";
    let start = data?.start ? moment(data?.start).format("DD/MM/YY") : "";
    let end = data?.end ? moment(data?.end).format("DD/MM/YY") : "";
    return {
      customerId: data?.hotspotCustomer
        ? data?.hotspotCustomer?.customerId
        : data?.customer?.customerId,
      name: data?.hotspotCustomer
        ? data?.hotspotCustomer?.name
        : data?.customer?.name,
      pppoe:
        data.customer?.userType === "pppoe"
          ? data.customer?.pppoe?.name
          : data.customer?.userType === "static"
            ? data.customer?.queue?.target
            : data?.hotspotCustomer?.hotspot?.name,
      package: data?.customer?.mikrotikPackage?.name
        ? data.customer?.mikrotikPackage?.name
        : data.customer?.userType === "pppoe"
          ? data.customer?.pppoe?.profile
          : data.hotspotCustomer?.hotspot.profile,
      amount: data.amount,
      due: data.due,
      medium: data.medium,
      collector: data.name,
      comment: note + " - " + start + " - " + end,
      createdAt: moment(data.createdAt).format("MM/DD/YYYY"),
    };
  });

  // set csv header
  const reportForCsVTableInfoHeader = [
    { label: "Customer Id", key: "customerId" },
    { label: "Name", key: "name" },
    { label: "PPPoE", key: "pppoe" },
    { label: "Package", key: "package" },
    { label: "Bill_Amount", key: "amount" },
    { label: "Bill_Due", key: "due" },
    { label: "Bill_Medium", key: "medium" },
    { label: "Collector_Name", key: "collector" },
    { label: "Comment", key: "comment" },
    { label: "Bill_Collect_Date", key: "createdAt" },
  ];

  const columns = useMemo(
    () => [
      {
        width: "6%",
        Header: t("id"),
        accessor: (field) =>
          field?.hotspotCustomer
            ? field?.hotspotCustomer?.customerId
            : field?.customer?.customerId,
        Cell: ({ row: { original } }) => (
          <div>
            <p className="text-center">
              {original?.hotspotCustomer
                ? original?.hotspotCustomer?.customerId
                : original?.customer?.customerId}
            </p>
            <Badge
              bg={
                original.customer?.userType === "pppoe"
                  ? "primary"
                  : original.customer?.userType === "static"
                    ? "info"
                    : "secondary"
              }
            >
              {original.customer?.userType === "pppoe"
                ? "PPPoE"
                : original.customer?.userType === "static"
                  ? "Static"
                  : original.hotspotCustomer?.userType === "hotspot"
                    ? "Hotspot"
                    : original.hotspotCustomer?.userType}
            </Badge>
          </div>
        ),
      },
      {
        width: "15%",
        Header: t("pppoeIp"),
        accessor: (field) =>
          `${
            field?.hotspotCustomer
              ? field?.hotspotCustomer?.name
              : field?.customer?.name
          } ${
            field.customer?.userType === "pppoe"
              ? field.customer?.pppoe?.name
              : field.customer?.userType === "static"
                ? field.customer?.queue?.target
                : field?.hotspotCustomer?.hotspot?.name
          }`,
        Cell: ({ row: { original } }) => {
          const customer = original?.customer;
          const hotspotCustomer = original?.hotspotCustomer;

          const name = hotspotCustomer?.name || customer?.name;

          let addressInfo = "";
          if (customer?.userType === "pppoe") {
            addressInfo = customer?.pppoe?.name;
          } else if (customer?.userType === "static") {
            addressInfo = customer?.queue?.target;
          } else {
            addressInfo = hotspotCustomer?.hotspot?.name;
          }

          return (
            <div className="text-start">
              <p>{name}</p>
              <p>{addressInfo}</p>
            </div>
          );
        },
      },
      {
        width: "14%",
        Header: t("packageBill"),
        accessor: (field) =>
          field.customer?.mikrotikPackage?.name
            ? field.customer?.mikrotikPackage?.name
            : field.customer?.userType === "pppoe"
              ? field.customer?.pppoe?.profile
              : field?.hotspotCustomer?.hotspot.profile,
        Cell: ({ row: { original } }) => {
          return (
            <div>
              <p>
                {original.customer?.userType === "pppoe"
                  ? original.customer?.pppoe?.profile
                  : original.customer?.userType === "static"
                    ? `${
                        original.customer?.uploadPackage
                          ? original.customer?.uploadPackage?.name
                          : original.customer?.mikrotikPackage?.name
                      }/${
                        original.customer?.downloadPackage
                          ? original.customer?.downloadPackage?.name
                          : original.customer?.mikrotikPackage?.name
                      }`
                    : original.hotspotCustomer?.hotspotPackage?.name}
              </p>
              <p style={{ fontWeight: "500" }}>৳{original?.amount}</p>
            </div>
          );
        },
      },
      {
        width: "9%",
        Header: t("discount"),
        accessor: "discount",
      },
      {
        width: "6%",
        Header: t("due"),
        accessor: "due",
      },
      {
        width: "10%",
        Header: t("TypeMedium"),
        accessor: (field) => `${field?.billingType} ${field?.medium}`,
        Cell: ({ row: { original } }) => {
          return (
            <div>
              <p style={{ fontWeight: "500" }}>{original?.medium}</p>
              <p>{badge(original?.billingType)}</p>
            </div>
          );
        },
      },
      {
        width: "12%",
        Header: t("collected"),
        accessor: "collectedBy",
        Cell: ({ row: { original } }) => {
          return (
            <div>
              <p style={{ fontSize: "14px", fontWeight: 500 }}>
                {original.name?.length > 15
                  ? original.name.slice(0, 15) + "..."
                  : original.name}
              </p>
              <p>{badge(original?.collectedBy)}</p>
            </div>
          );
        },
      },
      {
        width: "12%",
        Header: t("note"),
        accessor: "month",
        Cell: ({ row: { original } }) => {
          return (
            <div style={{ fontSize: "15px" }}>
              {original?.month && (
                <span>
                  {original?.month?.split(",").length > 1 ? (
                    <>
                      {original.month.split(",")[0] +
                        `${original?.note ? ". " : ""}`}
                      <span
                        className="see_more"
                        onClick={() => {
                          setViewId(original?._id);
                          setModalStatus("reportView");
                          setShow(true);
                        }}
                      >
                        ...see more
                      </span>
                    </>
                  ) : (
                    original.month + `${original?.note ? ". " : ""}`
                  )}
                </span>
              )}

              {original?.note && (
                <span>
                  {original.note.length > 15 ? (
                    <>
                      {original.note.slice(0, 15)}
                      <span
                        className="see_more"
                        onClick={() => {
                          setViewId(original?._id);
                          setModalStatus("reportView");
                          setShow(true);
                        }}
                      >
                        ...See More
                      </span>
                    </>
                  ) : (
                    original.note
                  )}
                </span>
              )}
            </div>
          );
        },
      },
      {
        width: "10%",
        Header: t("date"),
        accessor: "createdAt",
        Cell: ({ cell: { value } }) => {
          return moment(value).format("YYYY/MM/DD hh:mm A");
        },
      },
      {
        width: "6%",
        Header: () => <div className="text-center">{t("action")}</div>,
        id: "option",
        Cell: ({ row: { original } }) => (
          <div className="d-flex justify-content-center align-items-center">
            <div className="dropdown">
              <ThreeDots
                className="dropdown-toggle ActionDots"
                id="areaDropdown"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              />
              <ul className="dropdown-menu" aria-labelledby="customerDrop">
                <li
                  onClick={() => {
                    getReportId(original?._id);
                    setModalStatus("reportEdit");
                    setShow(true);
                  }}
                >
                  <div className="dropdown-item">
                    <div className="customerAction">
                      <PenFill />
                      <p className="actionP">{t("edit")}</p>
                    </div>
                  </div>
                </li>

                {(original.customer === null ||
                  original.hotspotCustomer === null) && (
                  <li
                    onClick={() => collectionReportDeleteHandler(original?._id)}
                  >
                    <div className="dropdown-item">
                      <div className="customerAction">
                        <ArchiveFill />
                        <p className="actionP">{t("delete")}</p>
                      </div>
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>
        ),
      },
    ],
    [t],
  );

  // data amount count in data table hader
  const addAllBills = useMemo(() => {
    var count = 0;
    let connectionFeeSum = 0;
    let discount = 0;
    billReport?.data.forEach((item) => {
      item.billType === "bill"
        ? (count += item.amount)
        : (connectionFeeSum += item.amount);

      discount = discount + item.discount;
    });
    return { count, connectionFeeSum, discount };
  }, [billReport]);

  // custom component for table header
  const customComponent = (
    <div
      className="text-center"
      style={{ fontSize: "18px", fontWeight: "500", display: "flex" }}
    >
      {(role === "ispOwner" || permissions?.dashboardCollectionData) &&
        addAllBills.count > 0 && (
          <div>
            {t("totalBill")}: ৳{FormatNumber(addAllBills.count)}
          </div>
        )}
      &nbsp;&nbsp;
      {(role === "ispOwner" || permissions?.dashboardCollectionData) &&
        addAllBills.connectionFeeSum > 0 && (
          <div>
            {t("connectionFee")}: ৳{FormatNumber(addAllBills.connectionFeeSum)}
          </div>
        )}
      &nbsp;&nbsp;
      {addAllBills.discount > 0 && (
        <div>
          {t("discount")}: ৳{FormatNumber(addAllBills.discount)}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Sidebar />
      <ToastContainer position="top-right" theme="colored" />

      <div className={useDash.dashboardWrapper}>
        <div className="container-fluied collector">
          <div className="container">
            <FontColor>
              <FourGround>
                <div className="collectorTitle d-flex justify-content-between px-4">
                  <h2>{t("billReport")}</h2>

                  <div
                    style={{ height: "45px" }}
                    className="d-flex align-items-center"
                  >
                    <div
                      onClick={() => {
                        if (!activeKeys) {
                          setActiveKeys("filter");
                        } else {
                          setActiveKeys("");
                        }
                      }}
                      title={t("filter")}
                    >
                      <FilterCircle className="addcutmButton" />
                    </div>

                    <div className="reloadBtn">
                      {isLoading ? (
                        <Loader />
                      ) : (
                        <ArrowClockwise
                          className="arrowClock"
                          onClick={() => reloadHandler()}
                          title={t("refresh")}
                        />
                      )}
                    </div>

                    <div className="addAndSettingIcon">
                      <PlayBtn
                        className="addcutmButton"
                        onClick={() => {
                          setModalStatus("playTutorial");
                          setShow(true);
                        }}
                        title={t("tutorial")}
                      />
                    </div>

                    <Collapse in={open} dimension="width">
                      <div id="example-collapse-text">
                        <Card className="cardCollapse border-0">
                          <div className="d-flex align-items-center">
                            <div className="addAndSettingIcon">
                              <CSVLink
                                data={reportForCsVTableInfo}
                                filename={userData.company}
                                headers={reportForCsVTableInfoHeader}
                                title="Bill Report"
                              >
                                <FiletypeCsv className="addcutmButton" />
                              </CSVLink>
                            </div>

                            <div className="addAndSettingIcon">
                              <ReactToPrint
                                documentTitle={t("billReport")}
                                trigger={() => (
                                  <PrinterFill
                                    title={t("print")}
                                    className="addcutmButton"
                                  />
                                )}
                                content={() => componentRef.current}
                              />
                            </div>
                          </div>
                        </Card>
                      </div>
                    </Collapse>

                    {!open && (
                      <ArrowBarLeft
                        className="ms-1"
                        size={34}
                        style={{ cursor: "pointer" }}
                        onClick={() => setOpen(!open)}
                        aria-controls="example-collapse-text"
                        aria-expanded={open}
                      />
                    )}

                    {open && (
                      <ArrowBarRight
                        className="ms-1"
                        size={34}
                        style={{ cursor: "pointer" }}
                        onClick={() => setOpen(!open)}
                        aria-controls="example-collapse-text"
                        aria-expanded={open}
                      />
                    )}
                  </div>
                </div>
              </FourGround>

              <FourGround>
                <div className="mt-2">
                  <Accordion alwaysOpen activeKey={activeKeys}>
                    <Accordion.Item eventKey="filter">
                      <Accordion.Body>
                        <DataFilter
                          filterData={allBills}
                          setFilterData={setBillReport}
                          filterOptions={filterOptions}
                          setFilterOption={setFilterOption}
                          inputPermission={filterInputPermission}
                          serverFiltering={true}
                          filterFunction={filterBillsReport}
                        />
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>

                  <div className="collectorWrapper pb-2">
                    <div className="addCollector">
                      <div style={{ display: "none" }}>
                        <PrintReport
                          filterData={filterDataHeader}
                          currentCustomers={billReport?.data}
                          ref={componentRef}
                          status="report"
                        />
                      </div>

                      <div className="table-section">
                        <PaginatedTable
                          isLoading={isLoading}
                          customComponent={customComponent}
                          columns={columns}
                          data={billReport}
                          // pageIndex={pageIndex}
                          // setPageIndex={setPageIndex}
                          // pageSize={pageSize}
                          // setPageSize={setPageSize}
                          callPaginationApi={fetchPaginatedData}
                          callTableQueryApi={handleFetchServerFiltered}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {(butPermission?.collectionReport ||
                  butPermission?.allPage) && <NetFeeBulletin />}
              </FourGround>
              <Footer />
            </FontColor>
          </div>
        </div>
      </div>

      {/* report edit modal */}
      {modalStatus === "reportEdit" && (
        <EditReport
          show={show}
          setShow={setShow}
          reportId={reportId}
          note={note}
          setNote={setNote}
          status="ispOwnerCustomerReport"
        />
      )}

      {/* report view modal */}
      {modalStatus === "reportView" && (
        <ReportView
          show={show}
          setShow={setShow}
          reportId={viewId}
          status="ispOwnerCustomerReport"
        />
      )}

      {/* tutorial play modal */}
      {modalStatus === "playTutorial" && (
        <PlayTutorial
          {...{
            show,
            setShow,
            video: "report",
          }}
        />
      )}
    </>
  );
};

export default Report;
